const SUPER_ADMIN_ROLES = ['DSI1', 'DSI2', 'ASIN1', 'ASIN2'];
const AGLO_ROLES = ['AGLO1', 'AGLO2'];
const AGENCY_ROLES = ['SA1', 'SA2'];
const MS_ROLES = ['MS1', 'MS2'];
const MAEC_ROLES = ['MAEC1', 'MAEC2'];
const DEI_ROLES = ['DEI1', 'DEI2'];
const BANK_ROLES = ['BANK1', 'BANK2'];
const PILGRIM_ROLES = ['PILGRIM1'];
const PRIMARY_ROLES = ['AGLO1', 'SA1', 'MS1', 'MAEC1', 'DEI1', 'BANK1'];

const toArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

// Payload normalization ------------------------------------------------------
//
// The API returns parallel arrays: roles[i] <-> organization_ids[i] <->
// organization_names[i].
export const getNormalizedRoles = (user) => toArray(user?.roles);

export const getNormalizedOrganizationIds = (user) => toArray(user?.organization_ids);

export const getNormalizedOrganizationNames = (user) => toArray(user?.organization_names);

export const hasPairedMemberships = (user) => {
  // Validate the original positions. Filtering empty entries can manufacture
  // a role/organization pairing that the API never supplied.
  const roles = user?.roles;
  const organizationIds = user?.organization_ids;

  return (
    Array.isArray(roles) &&
    Array.isArray(organizationIds) &&
    roles.length > 0 &&
    roles.length === organizationIds.length &&
    roles.every((role) => typeof role === 'string' && role.trim().length > 0) &&
    organizationIds.every((id) => typeof id === 'string' && id.trim().length > 0) &&
    new Set(organizationIds).size === organizationIds.length
  );
};

// Membership resolution ------------------------------------------------------
export const getOrganizationMemberships = (user) => {
  // Keep original indexes intact here. Filtering the parallel arrays before
  // zipping can manufacture a role/organization pairing around an empty slot.
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const organizationIds = Array.isArray(user?.organization_ids) ? user.organization_ids : [];
  const organizationNames = Array.isArray(user?.organization_names) ? user.organization_names : [];
  const length = Math.max(roles.length, organizationIds.length, organizationNames.length);

  return Array.from({ length }, (_, index) => ({
    index,
    role: typeof roles[index] === 'string' && roles[index].trim() ? roles[index].trim() : null,
    organizationId:
      typeof organizationIds[index] === 'string' && organizationIds[index].trim()
        ? organizationIds[index].trim()
        : null,
    organizationName:
      typeof organizationNames[index] === 'string' && organizationNames[index].trim()
        ? organizationNames[index].trim()
        : null,
  })).filter(({ role, organizationId, organizationName }) => role || organizationId || organizationName);
};

export const resolveActiveOrganizationId = (user) => user?.active_organization_id || null;

export const getActiveOrganizationMembership = (user) => {
  const memberships = getOrganizationMemberships(user);
  const activeOrganizationId = resolveActiveOrganizationId(user);

  if (!activeOrganizationId || !hasPairedMemberships(user)) {
    return null;
  }

  return memberships.find((membership) => membership.organizationId === activeOrganizationId) || null;
};

export const resolveActiveRole = (user) => {
  if (!hasPairedMemberships(user)) {
    return null;
  }

  return getActiveOrganizationMembership(user)?.role || null;
};

export const resolveActiveOrganizationName = (user) => getActiveOrganizationMembership(user)?.organizationName || null;

export const getUserMembershipWriteFields = (user, organizationIds) => ({
  organization_ids: organizationIds ?? getNormalizedOrganizationIds(user),
  membership_version: user?.membership_version ?? 0,
});

// Role booleans --------------------------------------------------------------
//
// Pages and route guards still expect the old boolean names. We keep those
// names here, but compute them from the resolved active role instead of from
// any role that merely exists on the user.
export const getRoleBooleans = (activeRole) => {
  const isSuperAdmin = SUPER_ADMIN_ROLES.includes(activeRole);
  const isAGLO = AGLO_ROLES.includes(activeRole);
  const isAGLOPrimary = activeRole === 'AGLO1';
  const isAGLOSecondary = activeRole === 'AGLO2';
  const isAgency = AGENCY_ROLES.includes(activeRole);
  const isAgencyPrimary = activeRole === 'SA1';
  const isAgencySecondary = activeRole === 'SA2';
  const isMS = MS_ROLES.includes(activeRole);
  const isMSPrimary = activeRole === 'MS1';
  const isMSSecondary = activeRole === 'MS2';
  const isMAEC = MAEC_ROLES.includes(activeRole);
  const isMAECPrimary = activeRole === 'MAEC1';
  const isMAECSecondary = activeRole === 'MAEC2';
  const isDEI = DEI_ROLES.includes(activeRole);
  const isDEIPrimary = activeRole === 'DEI1';
  const isDEISecondary = activeRole === 'DEI2';
  const isBank = BANK_ROLES.includes(activeRole);
  const isBankPrimary = activeRole === 'BANK1';
  const isBankSecondary = activeRole === 'BANK2';
  const isPilgrim = PILGRIM_ROLES.includes(activeRole);
  const isPilgrimPrimary = activeRole === 'PILGRIM1';
  const isPrimaryUser = isSuperAdmin || PRIMARY_ROLES.includes(activeRole);
  const canAccessDashboard = isAGLO || isSuperAdmin || isAgency || isMS || isMAEC || isBank;

  return {
    isSuperAdmin,
    isAGLO,
    isAGLOPrimary,
    isAGLOSecondary,
    isAgency,
    isAgencyPrimary,
    isAgencySecondary,
    isMS,
    isMSPrimary,
    isMSSecondary,
    isMAEC,
    isMAECPrimary,
    isMAECSecondary,
    isDEI,
    isDEIPrimary,
    isDEISecondary,
    isBank,
    isBankPrimary,
    isBankSecondary,
    isPilgrim,
    isPilgrimPrimary,
    isPrimaryUser,
    canAccessDashboard,
  };
};

export const getMembershipRolePresentation = (role) => {
  if (role === 'DSI1' || role === 'DSI2') return { typeKey: 'dsi' };
  if (role === 'ASIN1' || role === 'ASIN2') return { typeKey: 'asin' };
  if (AGLO_ROLES.includes(role)) return { typeKey: 'aglo' };
  if (role === 'SA1') return { typeKey: 'agency', levelKey: 'primary' };
  if (role === 'SA2') return { typeKey: 'agency', levelKey: 'secondary' };
  if (MS_ROLES.includes(role)) return { typeKey: 'health' };
  if (MAEC_ROLES.includes(role)) return { typeKey: 'passports' };
  if (DEI_ROLES.includes(role)) return { typeKey: 'dei' };
  if (role === 'BANK1') return { typeKey: 'bank' };
  if (role === 'BANK2') return { typeKey: 'bank', levelKey: 'secondary' };
  if (PILGRIM_ROLES.includes(role)) return { typeKey: 'pilgrim' };
  return null;
};

// Provider payload -----------------------------------------------------------
export const buildUserRoleState = (user) => {
  const roles = getNormalizedRoles(user);
  const organizationIds = getNormalizedOrganizationIds(user);
  const organizationNames = getNormalizedOrganizationNames(user);
  const memberships = getOrganizationMemberships(user);
  const activeOrganizationId = resolveActiveOrganizationId(user);
  const activeOrganizationName = resolveActiveOrganizationName(user);
  const activeRole = resolveActiveRole(user);

  return {
    roles,
    organizationIds,
    organizationNames,
    memberships,
    activeOrganizationId,
    activeOrganizationName,
    activeRole,
    hasMultipleOrganizations: organizationIds.length > 1,
    ...getRoleBooleans(activeRole),
  };
};
